import { useUnsavedChanges } from "../../layout/AppShellContext";
import { useActiveCompanyId } from "../../api/useActiveCompanyId";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MenuHeader } from "../Dashboard/block/MenuHeader/MenuHeader";
import { MobileMenu } from "../Dashboard/MobileMenu";
import { api } from "../../api/client";
import {
    UploadBatchProgress,
    uploadFilesToApiBestEffort,
} from "../shared/uploadFiles";
import { FileUploadField } from "../shared/FileUploadField";
import "../shared/mobileSection.scss";

type SupportInfoResponse = {
    data?: {
        address?: Array<{ id: number; address: string; name?: string }>;
        serviceTypes?: string[];
        selectedCompanyId?: number | null;
    };
};

type CreateResponse = {
    data?: {
        id?: number;
    };
};

// TaskBridge waits for Support for at most 20 seconds. Keep the browser timeout
// slightly longer so that it receives the gateway's final response instead of
// aborting an otherwise completed create request at the global 15-second limit.
const SUPPORT_TICKET_CREATE_TIMEOUT_MS = 25000;

export const SupportTicketCreatePage: React.FC = () => {
    const { value: activeCompanyId } = useActiveCompanyId();
    const generation = useRef(0);
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [companies, setCompanies] = useState<Array<{ id: number; address: string }>>([]);
    const [serviceTypes, setServiceTypes] = useState<string[]>([]);
    const [companyId, setCompanyId] = useState<string>("");
    const [serviceType, setServiceType] = useState("");
    const [problemDescription, setProblemDescription] = useState("");
    const [contactPhone, setContactPhone] = useState("");
    const [comment, setComment] = useState("");
    const [terminalPaymentBlocked, setTerminalPaymentBlocked] = useState(false);
    const [files, setFiles] = useState<File[]>([]);
    const [uploadProgress, setUploadProgress] = useState<UploadBatchProgress | null>(null);
    const [uploadFinished, setUploadFinished] = useState(false);
    const [selectionBaseline, setSelectionBaseline] = useState("");

    useUnsavedChanges(!loading && !success && (saving || JSON.stringify([companyId, serviceType]) !== selectionBaseline || !!problemDescription.trim() || !!contactPhone.trim() || !!comment.trim() || terminalPaymentBlocked || files.length > 0), "Обращение в поддержку");
    useEffect(() => {
        let alive = true; const counter = generation; ++counter.current;
        setSaving(false); setUploadProgress(null); setUploadFinished(false);
        setError(null); setSuccess(null); setProblemDescription(""); setContactPhone(""); setComment(""); setTerminalPaymentBlocked(false); setFiles([]);
        setLoading(true);
        api.get<SupportInfoResponse>("/api/gateway/tasks/info")
            .then((response) => {
                if (!alive) {
                    return;
                }

                const nextCompanies = response?.data?.address ?? [];
                const nextServiceTypes = response?.data?.serviceTypes ?? [];
                const defaultCompanyId = nextCompanies.find(item => String(item.id) === activeCompanyId)?.id ?? response?.data?.selectedCompanyId;

                setCompanies(nextCompanies.map((item) => ({
                    id: item.id,
                    address: item.address,
                })));
                setServiceTypes(nextServiceTypes);
                setCompanyId(defaultCompanyId ? String(defaultCompanyId) : (nextCompanies[0]?.id ? String(nextCompanies[0].id) : ""));
                setServiceType(nextServiceTypes[0] ?? "");
                setSelectionBaseline(JSON.stringify([defaultCompanyId ? String(defaultCompanyId) : String(nextCompanies[0]?.id || ""), nextServiceTypes[0] ?? ""]));
            })
            .catch((requestError: any) => {
                if (alive) {
                    setError(requestError?.message ?? "Не удалось загрузить форму");
                }
            })
            .finally(() => {
                if (alive) {
                    setLoading(false);
                }
            });

        return () => {
            alive = false; ++counter.current;
        };
    }, [activeCompanyId]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (saving || success) return;
        const epoch = generation.current;
        const config = { headers: { "X-Company-Id": activeCompanyId || companyId } };
        if (!companyId || !serviceType || !problemDescription.trim()) {
            setError("Заполните точку, тип проблемы и описание");
            return;
        }

        try {
            setSaving(true);
            setError(null);
            setSuccess(null);
            setUploadProgress(null);
            setUploadFinished(false);

            // The ticket is the durable part of this operation. Create it first;
            // a slow or broken photo upload must never discard the request.
            const response = await api.post<CreateResponse>(
                "/api/gateway/tasks",
                {
                    companyId: Number(companyId),
                    serviceType,
                    problemDescription: problemDescription.trim(),
                    contactPhone: contactPhone.trim(),
                    comment: comment.trim(),
                    terminalPaymentBlocked,
                    files: [],
                },
                { ...config, timeout: SUPPORT_TICKET_CREATE_TIMEOUT_MS },
            );

            if (epoch !== generation.current) return;
            const nextId = response?.data?.id;
            setSuccess(files.length > 0
                ? "Обращение создано, загружаем вложения..."
                : "Обращение создано.");
            let attachmentWarning = "";
            if (files.length > 0) {
                const uploadResult = await uploadFilesToApiBestEffort(files, progress => { if (epoch === generation.current) setUploadProgress(progress); });
                if (epoch !== generation.current) return;
                setUploadFinished(true);
                if (nextId && uploadResult.urls.length > 0) {
                    try {
                        await api.post(`/api/gateway/tasks/${nextId}/messages`, {
                            body: "Вложения к обращению",
                            files: uploadResult.urls,
                        }, config);
                    } catch {
                        attachmentWarning = " Вложения загрузились, но не прикрепились; добавьте их в переписке.";
                    }
                }
                if (uploadResult.failedFiles.length > 0) {
                    attachmentWarning = ` Не удалось загрузить файлов: ${uploadResult.failedFiles.length}; обращение уже сохранено.`;
                } else if (!nextId && uploadResult.urls.length > 0) {
                    attachmentWarning = " Вложения загрузились, но ответ сервера не содержал номер обращения; добавьте их в переписке.";
                }
            }

            if (epoch !== generation.current) return;
            setSuccess(`Обращение создано.${attachmentWarning}`);
            window.setTimeout(() => {
                if (epoch !== generation.current) return;
                navigate(nextId ? `/support/ticket/${nextId}` : "/support");
            }, attachmentWarning ? 1800 : 300);
        } catch (submitError: any) {
            if (epoch !== generation.current) return;
            setError(submitError?.message ?? "Не удалось создать обращение");
        } finally {
            if (epoch === generation.current) setSaving(false);
        }
    };

    return (
        <div className="section-page">
            <MenuHeader onMenuClick={() => setMenuOpen(true)} />
            <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

            <h1 className="section-title">Новое обращение в поддержку</h1>

            {loading ? <div className="section-empty">Загрузка формы...</div> : null}
            {error ? <div className="section-error">{error}</div> : null}
            {success ? <div className="section-success">{success}</div> : null}

            {!loading ? (
                <div className="section-card">
                    <form className="section-form" onSubmit={handleSubmit}>
                        <div className="section-field">
                            <label className="section-label" htmlFor="support-field-1">Точка</label>
                            <select id="support-field-1"
                                className="section-select"
                                value={companyId}
                                onChange={(event) => setCompanyId(event.target.value)}
                            >
                                {companies.map((company) => (
                                    <option key={company.id} value={company.id}>
                                        {company.address}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="section-field">
                            <label className="section-label" htmlFor="support-field-2">Тип проблемы</label>
                            <select id="support-field-2"
                                className="section-select"
                                value={serviceType}
                                onChange={(event) => setServiceType(event.target.value)}
                            >
                                {serviceTypes.map((item) => (
                                    <option key={item} value={item}>
                                        {item}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="section-field">
                            <label className="section-label" htmlFor="support-field-3">Описание проблемы</label>
                            <textarea id="support-field-3"
                                className="section-textarea"
                                value={problemDescription}
                                onChange={(event) => setProblemDescription(event.target.value)}
                                placeholder="Что случилось, когда началось, как воспроизвести"
                            />
                        </div>

                        <label className="section-checkbox">
                            <input
                                type="checkbox"
                                checked={terminalPaymentBlocked}
                                onChange={(event) => setTerminalPaymentBlocked(event.target.checked)}
                            />
                            <span className="section-checkbox-text">
                                <span>Терминал не принимает оплаты</span>
                                <span className="section-priority-note">(Эта задача будет взята в приоритет)</span>
                            </span>
                        </label>

                        <div className="section-field">
                            <label className="section-label" htmlFor="support-field-4">Телефон для связи</label>
                            <input id="support-field-4"
                                className="section-input"
                                type="tel"
                                value={contactPhone}
                                onChange={(event) => setContactPhone(event.target.value)}
                                placeholder="+7 999 123-45-67"
                                inputMode="tel"
                            />
                            <div className="section-form-hint">
                                По закрытию тикета или если возникнут вопросы, мы вам позвоним.
                            </div>
                        </div>

                        <div className="section-field">
                            <label className="section-label" htmlFor="support-field-5">Комментарий</label>
                            <textarea id="support-field-5"
                                className="section-textarea"
                                value={comment}
                                onChange={(event) => setComment(event.target.value)}
                                placeholder="Дополнительная информация для поддержки"
                            />
                        </div>

                        <FileUploadField
                            label="Вложения"
                            hint="Обращение сначала сохранится. Затем фото, PDF и другие файлы загрузятся и прикрепятся к нему."
                            files={files}
                            onChange={setFiles}
                            disabled={saving}
                        />

                        {uploadProgress ? (
                            <div className="section-upload-progress" aria-live="polite">
                                <div className="section-upload-progress-heading">
                                    <span>{uploadFinished ? "Загрузка завершена" : "Загрузка вложений"}</span>
                                    <strong>{uploadProgress.totalPercent}%</strong>
                                </div>
                                <div
                                    className="section-upload-progress-track"
                                    role="progressbar"
                                    aria-label="Загрузка вложений"
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                    aria-valuenow={uploadProgress.totalPercent}
                                >
                                    <span
                                        className="section-upload-progress-fill"
                                        style={{ width: `${uploadProgress.totalPercent}%` }}
                                    />
                                </div>
                                <div className="section-upload-progress-file">
                                    <span>Файл {uploadProgress.fileNumber} из {uploadProgress.totalFiles}:</span>
                                    <span title={uploadProgress.fileName}>{uploadProgress.fileName}</span>
                                </div>
                            </div>
                        ) : null}

                        <div className="section-actions">
                            <button className="section-btn" type="submit" disabled={saving}>
                                {saving
                                    ? (uploadProgress ? `Загрузка ${uploadProgress.totalPercent}%` : "Создаем...")
                                    : "Создать обращение"}
                            </button>
                            <button
                                className="section-btn-secondary"
                                type="button"
                                disabled={saving}
                                onClick={() => navigate("/support")}
                            >
                                Отмена
                            </button>
                        </div>
                    </form>
                </div>
            ) : null}
        </div>
    );
};
